import { FinancePaymentStatus, RenderedServiceEventName } from 'chaika-enums';
import { FinancePaymentViewModel, RenderedServiceData } from 'chaika-frontend-types/frontend-cms';

import { FinancePaymentApi, RenderedServiceApi } from '@api';
import { useDownloadFile } from '@utils/hooks/useDownloadFile';
import { useDateParamsState } from './useDateParamsState';
import { useOrganizationHandlers } from './useOrganizationHandlers';
import { ReportType } from './useOptions';
import { useLegalEntityHandlers } from './useLegalEntityHandlers';
import { useReportTypesHandlers } from './useReportTypesHandlers';
import {
    FormattedPayments,
    FormattedRenderedService,
    FormattedRenderedServicesChanges,
    useFormatData,
} from './useFormatData';
import { patientService } from '@patient/services/patient/patientService';
import { serviceService } from '@services/services/serviceService';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { assertIsDefined } from '@utils/assertIsDefined';
import { store } from '@store';

const SELECTED_ORGANIZATIONS_MESSAGE = 'Report button is enabled only after organizations are selected';

interface ReportResult {
    rendered_services?: FormattedRenderedService[];
    payments?: FormattedPayments[];
    changes?: FormattedRenderedServicesChanges[];
}

export function useHandlers() {
    const { selectedLegalEntity, onSelectLegalEntity } = useLegalEntityHandlers();
    const { selectedOrganizations, organizationsWithoutStore, onSelectOrganizations } = useOrganizationHandlers();
    const { selectedReportTypes, onSelectReportType } = useReportTypesHandlers();
    const { formatRenderedServicesChanges, formatRenderedServices, formatPayments } = useFormatData();

    const { downloadFileFromBlobOrString } = useDownloadFile();
    const { dateParams, openToDate, maxDate, onChangePeriod } = useDateParamsState();

    const { app } = store;

    async function onCreateReportClick(): Promise<void> {
        app.setIsOnUpdate(true);

        const selectedReportTypesMap = new Map();

        assertIsDefined(selectedReportTypes, 'Report button is enabled only after report types are selected');
        selectedReportTypes.forEach(item => selectedReportTypesMap.set(item.value, item.label));

        const withRenderedServiceReport = selectedReportTypesMap.has(ReportType.RenderedServices);
        const withRenderedServiceChangesReport = selectedReportTypesMap.has(ReportType.RenderedServicesChanges);
        await Promise.all([
            selectedReportTypesMap.has(ReportType.Payments) && makePaymentReport(),
            withRenderedServiceReport && !withRenderedServiceChangesReport && makeRenderedServiceReport(),
            withRenderedServiceChangesReport && makeRenderedServiceEventReport(withRenderedServiceReport),
        ]);

        app.setIsOnUpdate(false);
    }

    async function makeRenderedServiceEventReport(withRenderedServiceReport: boolean = false): Promise<void> {
        assertIsDefined(selectedOrganizations, SELECTED_ORGANIZATIONS_MESSAGE);
        const { renderedServices } = await RenderedServiceApi.getRenderedServicesEvents({
            organizationId: selectedOrganizations.map(item => item.value).join(),
            createTimeFrom: dateParams.from.toISOString(),
            createTimeTo: dateParams.to.toISOString(),
            eventName: [
                RenderedServiceEventName.RenderedServiceCanceled,
                RenderedServiceEventName.RenderedServiceDeleted,
                RenderedServiceEventName.RenderedServicePaymentChanged,
            ].join(),
        });

        if (withRenderedServiceReport) {
            await makeRenderedServiceReportFromRenderedServoces(renderedServices);
        }

        const changes = formatRenderedServicesChanges(renderedServices);
        downloadReport({ changes }, ReportType.RenderedServicesChanges);
    }
    async function makeRenderedServiceReport(): Promise<void> {
        assertIsDefined(selectedOrganizations, SELECTED_ORGANIZATIONS_MESSAGE);
        const { renderedServices } = await RenderedServiceApi.getList({
            organizationId: selectedOrganizations.map(item => item.value).join(),
            createTimeFrom: dateParams.from.toISOString(),
            createTimeTo: dateParams.to.toISOString(),
            needPayment: false,
        });

        await makeRenderedServiceReportFromRenderedServoces(renderedServices);
    }

    async function makeRenderedServiceReportFromRenderedServoces(
        renderedServicesData: RenderedServiceData[],
    ): Promise<void> {
        const ehrIds = new Set<string>();
        const serviceIds = new Set<string>();
        renderedServicesData.forEach(({ ehrId, serviceId }) => {
            ehrIds.add(ehrId);
            serviceIds.add(serviceId);
        });
        await Promise.all([
            patientService.loadByIds(toArrayFromIterable(ehrIds)),
            serviceService.loadByIds(toArrayFromIterable(serviceIds)),
        ]);
        const rendered_services = formatRenderedServices(renderedServicesData);
        downloadReport({ rendered_services }, ReportType.RenderedServices);
    }

    async function makePaymentReport(): Promise<void> {
        assertIsDefined(selectedOrganizations, SELECTED_ORGANIZATIONS_MESSAGE);
        const { payments } = await FinancePaymentApi.find({
            status: FinancePaymentStatus.Completed,
            organizationId: selectedOrganizations.map(item => item.value).join(),
            from: dateParams.from.toISOString(),
            to: dateParams.to.toISOString(),
        });

        const [externalIdByRenderedServiceId] = await Promise.all([
            loadRenderedServiceExternalIds(payments),
            patientService.loadByIds(payments.map(payment => payment.patientId)),
        ]);

        const formattedPayments = formatPayments(payments, externalIdByRenderedServiceId);
        downloadReport({ payments: formattedPayments }, ReportType.Payments);
    }

    async function loadRenderedServiceExternalIds(payments: FinancePaymentViewModel[]): Promise<Map<string, number>> {
        const chunkedIds = getUniqChunkedIds(payments.flatMap(payment => payment.renderedServiceIds));
        const responses = await Promise.all(chunkedIds.map(ids => RenderedServiceApi.getList({ id: ids.join() })));

        return new Map(
            responses
                .flatMap(response => response.renderedServices)
                .map((service): [string, number] => [service.id, service.externalId]),
        );
    }

    function downloadReport(params: ReportResult, type: ReportType): void {
        const resultToDownload = JSON.stringify({ ...params }, undefined, 2);
        downloadFileFromBlobOrString(resultToDownload, type);
    }

    return {
        maxDate,
        openToDate,
        dateParams,
        selectedOrganizations,
        selectedLegalEntity,
        selectedReportTypes,
        organizationsWithoutStore,
        onChangePeriod,
        onCreateReportClick,
        onSelectLegalEntity,
        onSelectReportType,
        onSelectOrganizations,
    };
}
